import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  View,
  Text,
  ActivityIndicator,
  Pressable,
  Platform,
  ScrollView,
  useWindowDimensions,
} from 'react-native'
import { useLocalSearchParams, usePathname, useRouter, type Href } from 'expo-router'
import { useTheme } from '../../../theme/useTheme'
import { useSupabaseAuth } from '../../../shared/contexts/SupabaseAuthContext'
import { WEB_HOVER_TRANSITION_STYLE } from '../../../shared/constants/animations'
import { extractErrorMessage, useApiClient } from '../../../../helpers/apiClient.helper'
import { API_ENDPOINTS } from '../../../../constants/api'
import { getAccountAdminPageStyles } from '../../../../appStyles/_accountAdmin.styles'
import { PageHeader } from '../../../../components/layout'
import { HEADER_FLAVORS } from '../../../../components/layout/constants/pageHeader.constants'
import { useAccountAdmin } from '../../hooks/useAccountAdmin'
import { useConsumerBillingStripeSync } from '../../hooks/useConsumerBillingStripeSync'
import {
  ACCOUNT_ADMIN_NAV,
  ACCOUNT_ADMIN_PUBLIC_ROUTE,
  BILLING_GATE_ENABLED,
  type AccountAdminNavId,
} from '../../constants/accountAdmin.constants'
import { useAccountAdminCopy } from '../../hooks/useAccountAdminCopy'
import {
  getAccountAdminNavQueryKey,
  parseAccountAdminNavParam,
} from '../../helpers/accountAdminNav.helper'
import { ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX } from '../../constants/accountAdminNav.constants'
import { isB2bInviteJoinCodeForAccount, sanitizeJoinCodeInput } from '../../helpers/b2bAccount.helper'
import { AccountRegistrationForm } from '../AccountRegistrationForm/AccountRegistrationForm'
import { AccountAdminNavDrawer } from '../AccountAdminNavDrawer/AccountAdminNavDrawer'
import { AccountAdminSidebar } from '../AccountAdminSidebar/AccountAdminSidebar'
import { useAccountAdminSiteMenuOptional } from '../../contexts/AccountAdminSiteMenuContext'
import { useAccountAdminNavDrawerOpen } from '../../contexts/AccountAdminNavDrawerOpenContext'
import { useRegisterAccountAdminWebDrawerSiteLinks } from '../../contexts/AccountAdminWebDrawerSiteLinksContext'
import { useSyncAccountAdminDrawerWebPushPx } from '../../hooks/useSyncAccountAdminDrawerWebPushPx'
import { useAccountAdminDialogs } from '../../hooks/useAccountAdminDialogs'
import { AccountAdminTabContent } from './AccountAdminTabContent'
import type { AccountAdminSiteMenuLink } from '../../types/accountAdminNav.types'

export interface AccountAdminScreenProps {
  /** `site`: rendered under marketing nav (no in-app PageHeader). */
  surface?: 'site' | 'inApp'
  /** Shown in the org drawer when marketing links are not in the top bar. */
  siteMenuLinks?: AccountAdminSiteMenuLink[]
}

export function AccountAdminScreen({
  surface = 'inApp',
  siteMenuLinks,
}: AccountAdminScreenProps) {
  const copy = useAccountAdminCopy()
  const router = useRouter()
  const pathname = usePathname() ?? ACCOUNT_ADMIN_PUBLIC_ROUTE
  const params = useLocalSearchParams<{
    join?: string | string[]
    nav?: string | string[]
    billing?: string | string[]
    session_id?: string | string[]
  }>()
  const isSiteWeb = surface === 'site' && Platform.OS === 'web'
  const { width: windowWidth } = useWindowDimensions()
  const showSidebar = isSiteWeb && windowWidth >= ACCOUNT_ADMIN_SIDEBAR_BREAKPOINT_PX
  const appTheme = useTheme()
  const theme = appTheme
  const initialJoinFromInvite = useMemo(() => {
    const j = params.join
    if (typeof j === 'string' && j.trim()) return j.trim()
    if (Array.isArray(j) && j[0]?.trim()) return j[0].trim()
    return undefined
  }, [params.join])
  const styles = useMemo(() => getAccountAdminPageStyles(theme), [theme])
  const rootContainerStyle = useMemo(
    () => [
      styles.container,
      isSiteWeb ? styles.siteMarketingRootBg : null,
    ],
    [isSiteWeb, styles.container, styles.siteMarketingRootBg],
  )
  const { userId, user: authUser } = useSupabaseAuth()
  const apiClient = useApiClient()
  const {
    user,
    branding,
    members,
    loading,
    error,
    refresh,
    isAdmin,
    accountId,
    createOrganization,
    joinOrganization,
    patchBrandingColors,
    patchAccountSettings,
    saveSsoSettings,
    removeMember,
    removeMembersBulk,
    patchMemberRole,
    leaveOrganization,
    deleteOrganizationPermanently,
    canLeaveOrganization,
  } = useAccountAdmin()

  const [activeNav, setActiveNav] = useState<AccountAdminNavId>(ACCOUNT_ADMIN_NAV.START_HERE)
  const { drawerOpen: navDrawerOpen, setDrawerOpen: setNavDrawerOpen } =
    useAccountAdminNavDrawerOpen()
  const siteMenuBridge = useAccountAdminSiteMenuOptional()

  const renderOrgDrawerLocally = Platform.OS !== 'web' || surface === 'inApp'
  const shouldRegisterWebDrawerSiteLinks =
    Platform.OS === 'web' && surface === 'site' && siteMenuLinks != null

  useRegisterAccountAdminWebDrawerSiteLinks(
    shouldRegisterWebDrawerSiteLinks ? siteMenuLinks : null,
  )
  useSyncAccountAdminDrawerWebPushPx(renderOrgDrawerLocally, navDrawerOpen)

  useConsumerBillingStripeSync(refresh, {
    enabled:
      Platform.OS === 'web' &&
      Boolean(userId && user && user.account && user.membership && isAdmin),
    activeNav,
    planBillingNavMatch: ACCOUNT_ADMIN_NAV.PLAN_BILLING,
  })

  /**
   * `?nav=` is for org tabs only. While the user has no org (create/join form), ignore it so a stale
   * URL like `/account-admin?nav=settings` does not land them on Settings right after creating an org.
   */
  const skipNextNavParamSyncRef = useRef(false)
  /** Last `?nav=` key we applied to `activeNav` — avoids overwriting drawer picks when `user` refetches (e.g. after logo remove). */
  const lastSyncedNavQueryKeyRef = useRef<string | null>(null)

  useEffect(() => {
    if (!user) {
      lastSyncedNavQueryKeyRef.current = null
      setActiveNav(ACCOUNT_ADMIN_NAV.START_HERE)
      return
    }
    if (skipNextNavParamSyncRef.current) {
      skipNextNavParamSyncRef.current = false
      return
    }
    const navQueryKey = getAccountAdminNavQueryKey(params.nav)
    if (lastSyncedNavQueryKeyRef.current === navQueryKey) {
      return
    }
    lastSyncedNavQueryKeyRef.current = navQueryKey
    const n = parseAccountAdminNavParam(params.nav)
    if (n) {
      if (
        (n === ACCOUNT_ADMIN_NAV.PLAN_BILLING || n === ACCOUNT_ADMIN_NAV.INVOICE_DETAILS) &&
        Platform.OS !== 'web'
      ) {
        setActiveNav(ACCOUNT_ADMIN_NAV.START_HERE)
      } else {
        setActiveNav(n)
      }
    }
  }, [params.nav, user])

  const billingQueryFlag = useMemo(() => {
    const b = params.billing
    const raw = typeof b === 'string' ? b : Array.isArray(b) ? b[0] : undefined
    return typeof raw === 'string' ? raw.trim().toLowerCase() : ''
  }, [params.billing])

  const checkoutSessionId = useMemo(() => {
    const s = params.session_id
    const raw = typeof s === 'string' ? s : Array.isArray(s) ? s[0] : undefined
    return typeof raw === 'string' && raw.startsWith('cs_') ? raw : null
  }, [params.session_id])

  const billingHandledRef = useRef(false)
  const [b2bStripeReturnSyncing, setB2bStripeReturnSyncing] = useState(false)
  useEffect(() => {
    if (billingQueryFlag !== 'success' || !user || billingHandledRef.current) return
    billingHandledRef.current = true
    const syncAndRefresh = async () => {
      setB2bStripeReturnSyncing(true)
      try {
        if (accountId && checkoutSessionId) {
          try {
            await apiClient(API_ENDPOINTS.billing.b2bSyncCheckoutSession(accountId), {
              method: 'POST',
              data: { sessionId: checkoutSessionId },
            })
          } catch {
            // Webhook may have already applied; still refresh below.
          }
        }
        await refresh()
        /**
         * Prefer in-place query updates on web so Expo Router does not remount the whole screen
         * (router.replace to the same route can still feel like a full reload + retrigger focus effects).
         */
        const path = pathname ?? ''
        const isAccountAdminPath =
          path === ACCOUNT_ADMIN_PUBLIC_ROUTE ||
          path === '/app/account-admin' ||
          path.endsWith('/account-admin')
        if (Platform.OS === 'web' && isAccountAdminPath) {
          router.setParams({
            nav: ACCOUNT_ADMIN_NAV.MEMBERS,
            billing: undefined,
            session_id: undefined,
          })
          setActiveNav(ACCOUNT_ADMIN_NAV.MEMBERS)
        } else {
          router.replace(`${ACCOUNT_ADMIN_PUBLIC_ROUTE}?nav=${ACCOUNT_ADMIN_NAV.MEMBERS}` as Href)
        }
      } finally {
        setB2bStripeReturnSyncing(false)
      }
    }
    void syncAndRefresh()
  }, [
    billingQueryFlag,
    checkoutSessionId,
    user,
    accountId,
    refresh,
    router,
    apiClient,
    pathname,
  ])
  const [formError, setFormError] = useState<string | null>(null)
  const [formBusy, setFormBusy] = useState(false)

  const {
    memberLeaveBusy,
    memberActionPending,
    handleMemberLeaveOrganization,
    handleRemoveMember,
    handlePromoteToAdmin,
    handleDemoteFromAdmin,
  } = useAccountAdminDialogs({ removeMember, patchMemberRole, leaveOrganization })

  /** Sync selected org tab to `?nav=` on web so refresh keeps the same tab. */
  const handleSelectNav = useCallback(
    (id: AccountAdminNavId) => {
      setActiveNav(id)
      if (Platform.OS === 'web') {
        lastSyncedNavQueryKeyRef.current = getAccountAdminNavQueryKey(id)
        router.setParams({ nav: id })
      }
    },
    [router],
  )

  const handleCreate = useCallback(
    async (payload: Parameters<typeof createOrganization>[0]) => {
      setFormError(null)
      setFormBusy(true)
      try {
        await createOrganization(payload)
        skipNextNavParamSyncRef.current = true
        setActiveNav(ACCOUNT_ADMIN_NAV.PLAN_BILLING)
        router.replace(pathname as Href)
      } catch (e) {
        setFormError(extractErrorMessage(e, 'Could not create'))
      } finally {
        setFormBusy(false)
      }
    },
    [createOrganization, pathname, router],
  )

  const handleJoin = useCallback(
    async (code: string) => {
      setFormError(null)
      setFormBusy(true)
      try {
        const joinResult = await joinOrganization(code)
        skipNextNavParamSyncRef.current = true
        setActiveNav(ACCOUNT_ADMIN_NAV.START_HERE)
        const groupId = joinResult?.defaultCompetitionGroupId
        const wcOk = joinResult?.worldCupJoin == null || joinResult.worldCupJoin.ok === true
        if (typeof groupId === 'string' && groupId.length > 0 && wcOk) {
          router.replace(`/app/worldcup/groups/${groupId}` as Href)
          return
        }
        // Redirect to the squads list inside the app — not back to account-admin where the
        // user has no admin context and would see a sync error.
        router.replace('/app/worldcup/groups' as Href)
      } catch (e) {
        setFormError(extractErrorMessage(e, 'Could not join'))
      } finally {
        setFormBusy(false)
      }
    },
    [joinOrganization, pathname, router],
  )

  /** Invite links only pre-filled the code; without auto-join, users often submit "Create" and become ADMIN of a new org. */
  const inviteAutoJoinStartedRef = useRef(false)
  useEffect(() => {
    if (inviteAutoJoinStartedRef.current) return
    const raw = initialJoinFromInvite?.trim()
    if (!raw) return
    const code = sanitizeJoinCodeInput(raw)
    if (code.length < 4) return
    if (loading) return
    if (user) return
    if (!userId) return

    inviteAutoJoinStartedRef.current = true
    void handleJoin(code)
  }, [initialJoinFromInvite, loading, user, userId, handleJoin])

  const uid = userId ?? ''
  const showInAppHeader = surface === 'inApp'
  const contentStyle = isSiteWeb ? [styles.content, styles.siteMarketingContent] : styles.content
  const siteDecor = isSiteWeb ? (
    <>
      <View
        style={[styles.siteGlow, styles.siteGlowPrimary]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />
      <View
        style={[styles.siteGlow, styles.siteGlowGold]}
        accessibilityElementsHidden
        importantForAccessibility="no-hide-descendants"
      />
    </>
  ) : null

  useEffect(() => {
    if (surface !== 'site' || !siteMenuBridge) {
      return undefined
    }
    siteMenuBridge.registerOpenMenu(() => setNavDrawerOpen(true))
    return () => {
      siteMenuBridge.registerOpenMenu(null)
    }
  }, [surface, siteMenuBridge, setNavDrawerOpen])

  if (loading && !user) {
    return (
      <View style={rootContainerStyle}>
        {showInAppHeader ? <PageHeader flavor={HEADER_FLAVORS.FRIENDS} /> : null}
        <View style={styles.center}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      </View>
    )
  }

  if (error && !user) {
    return (
      <View style={rootContainerStyle}>
        {showInAppHeader ? <PageHeader flavor={HEADER_FLAVORS.FRIENDS} /> : null}
        <View style={styles.center}>
          <Text style={styles.sub}>{error}</Text>
        </View>
      </View>
    )
  }

  if (!user || !user.account || !user.membership) {
    return (
      <View style={rootContainerStyle}>
        {showInAppHeader ? <PageHeader flavor={HEADER_FLAVORS.FRIENDS} /> : null}
        <View style={contentStyle}>
          {siteDecor}
          {isSiteWeb ? (
            <ScrollView
              style={styles.siteWebMainScroll}
              contentContainerStyle={styles.siteCenteredScrollContent}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator
            >
              <View style={styles.siteEmptyCard}>
                <Text style={styles.headline}>{copy.NO_ORG_TITLE}</Text>
                <Text style={styles.sub}>{copy.NO_ORG_SUBTITLE}</Text>
                <AccountRegistrationForm
                  onSubmitCreate={handleCreate}
                  onSubmitJoin={handleJoin}
                  busy={formBusy}
                  error={formError}
                  initialJoinCode={initialJoinFromInvite}
                  creatorEmail={authUser?.primaryEmailAddress?.emailAddress ?? ''}
                  marketing
                  embedInOuterScroll
                />
              </View>
            </ScrollView>
          ) : (
            <View style={isSiteWeb ? styles.siteEmptyCard : undefined}>
              <Text style={styles.headline}>{copy.NO_ORG_TITLE}</Text>
              <Text style={styles.sub}>{copy.NO_ORG_SUBTITLE}</Text>
              <AccountRegistrationForm
                onSubmitCreate={handleCreate}
                onSubmitJoin={handleJoin}
                busy={formBusy}
                error={formError}
                initialJoinCode={initialJoinFromInvite}
                creatorEmail={authUser?.primaryEmailAddress?.emailAddress ?? ''}
              />
            </View>
          )}
        </View>
      </View>
    )
  }

  if (user.account && !isAdmin) {
    const openedOwnOrgInviteLink = isB2bInviteJoinCodeForAccount(
      initialJoinFromInvite,
      user.account.joinCode,
    )
    return (
      <View style={rootContainerStyle}>
        {showInAppHeader ? <PageHeader flavor={HEADER_FLAVORS.FRIENDS} /> : null}
        <View style={contentStyle}>
          {siteDecor}
          <View style={isSiteWeb ? styles.siteCenteredContent : styles.center}>
            <View style={isSiteWeb ? styles.siteEmptyCard : undefined}>
              <Text style={styles.headline}>
                {openedOwnOrgInviteLink
                  ? copy.MEMBER_INVITE_RETURN_TITLE
                  : copy.MEMBER_ONLY_TITLE}
              </Text>
              {user.account.displayName || user.account.name ? (
                <Text style={[styles.sub, { fontWeight: '700', marginBottom: 4 }]}>
                  {user.account.displayName || user.account.name}
                </Text>
              ) : null}
              <Text style={styles.sub}>
                {openedOwnOrgInviteLink
                  ? copy.MEMBER_INVITE_RETURN_MESSAGE
                  : copy.MEMBER_ONLY_MESSAGE}
              </Text>
              <Text style={styles.sub}>{copy.LEAVE_ORG_MEMBER_HINT}</Text>
              <Pressable
                accessibilityRole="button"
                onPress={() => (router.canGoBack() ? router.back() : router.replace('/app/friends'))}
                style={({ pressed }) => [styles.primaryBtn, pressed && styles.primaryBtnPressed]}
              >
                <Text style={styles.primaryBtnText}>OK</Text>
              </Pressable>
              <Pressable
                accessibilityRole="button"
                disabled={memberLeaveBusy}
                onPress={() => void handleMemberLeaveOrganization()}
                style={({ pressed, hovered }) => [
                  styles.outlineBtn,
                  Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                  pressed && styles.outlineBtnPressed,
                  hovered && Platform.OS === 'web' && styles.outlineBtnHover,
                  memberLeaveBusy && styles.outlineBtnDisabled,
                ]}
              >
                <Text style={styles.outlineBtnText}>{copy.LEAVE_ORG_CONFIRM}</Text>
              </Pressable>
            </View>
          </View>
        </View>
      </View>
    )
  }

  if (!accountId) {
    return null
  }

  /** RN Web: nested flex + inner ScrollView often clips; one outer scroll fixes `/account-admin` (site). */
  const siteWebOuterScroll = isSiteWeb
  const mainColumnStyle = siteWebOuterScroll
    ? styles.siteContentColumnInMainScroll
    : surface === 'site'
      ? styles.siteContentColumn
      : styles.contentInnerInApp

  const tabContent = (
    <AccountAdminTabContent
      activeNav={activeNav}
      surface={surface}
      showSidebar={showSidebar}
      siteWebOuterScroll={siteWebOuterScroll}
      user={user}
      accountId={accountId}
      branding={branding}
      isBrandingLoading={loading}
      members={members}
      uid={uid}
      canLeaveOrganization={canLeaveOrganization}
      styles={styles}
      theme={theme}
      onNavigateToNav={handleSelectNav}
      onOpenNavDrawer={() => setNavDrawerOpen(true)}
      onRefresh={refresh}
      patchBrandingColors={patchBrandingColors}
      patchAccountSettings={patchAccountSettings}
      saveSsoSettings={saveSsoSettings}
      onRemoveMember={handleRemoveMember}
      onPromoteToAdmin={handlePromoteToAdmin}
      onDemoteFromAdmin={handleDemoteFromAdmin}
      memberActionPending={memberActionPending}
      onRemoveMembersBulk={removeMembersBulk}
    leaveOrganization={leaveOrganization}
    deleteOrganizationPermanently={deleteOrganizationPermanently}
    b2bStripeReturnSyncing={b2bStripeReturnSyncing}
    />
  )

  const billingUnlocked = !BILLING_GATE_ENABLED || (user.account.planTier !== 'FREE' && user.account.planTier !== 'STARTER')

  /** Wide web: sidebar + content; narrow web/native: full-width content column. */
  const mainContent = showSidebar ? (
    <View style={styles.siteWebSidebarRow}>
      <AccountAdminSidebar
        activeNav={activeNav}
        onSelectNav={handleSelectNav}
        billingUnlocked={billingUnlocked}
      />
      <View style={styles.siteWebSidebarContent}>{tabContent}</View>
    </View>
  ) : (
    tabContent
  )

  return (
    <View style={rootContainerStyle}>
      {showInAppHeader ? <PageHeader flavor={HEADER_FLAVORS.FRIENDS} /> : null}
      <View style={contentStyle}>
        {siteDecor}
        {siteWebOuterScroll ? (
          <ScrollView
            style={styles.siteWebMainScroll}
            contentContainerStyle={styles.siteWebMainScrollContent}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator
          >
            <View style={mainColumnStyle}>{mainContent}</View>
          </ScrollView>
        ) : (
          <View style={mainColumnStyle}>{mainContent}</View>
        )}
      </View>
      {renderOrgDrawerLocally && !showSidebar ? (
        <AccountAdminNavDrawer
          visible={navDrawerOpen}
          activeNav={activeNav}
          onDismiss={() => setNavDrawerOpen(false)}
          onSelectNav={handleSelectNav}
          siteMenuLinks={siteMenuLinks}
          billingUnlocked={billingUnlocked}
        />
      ) : null}
    </View>
  )
}
